import { useState } from "react";
import { Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ArrowLeft, Play } from "lucide-react";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { ShowreelProvider, useShowreel } from "@/components/site/Showreel";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { resolveUrl } from "@/lib/media";

export const publishedVideos = (v) => (v?.items || []).filter((it) => (it.status || "published") !== "hidden");

const VideosBody = () => {
  const { content } = useContent();
  const v = content.videos || {};
  const showreel = useShowreel();
  const items = publishedVideos(v);
  const cats = ["Tout", ...(v.categories || [])];
  const [cat, setCat] = useState("Tout");
  const shown = cat === "Tout" ? items : items.filter((it) => it.category === cat);

  return (
    <div className="App bg-[#050505] min-h-screen flex flex-col">
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <Navbar />
      <main className="flex-1 pt-32 md:pt-40 pb-24" data-testid="videos-page">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Reveal className="mb-12 max-w-2xl">
            <Link to="/" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-secondary2 hover:text-electric transition-colors mb-8"><ArrowLeft size={14} /> Accueil</Link>
            <div className="flex items-center gap-3 mb-5"><span className="h-px w-10 bg-electric" /><span className="text-xs tracking-[0.35em] uppercase text-electric">{v.eyebrow || "Galerie vidéo"}</span></div>
            <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95] mb-6">{v.heading || "Nos films en mouvement."}</h1>
            {v.intro && <p className="text-base md:text-lg text-secondary2 font-light leading-relaxed">{v.intro}</p>}
          </Reveal>

          {(v.categories || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10">
              {cats.map((c) => (
                <button key={c} data-testid={`video-cat-${c}`} onClick={() => setCat(c)}
                  className={`px-4 py-1.5 rounded-full text-xs tracking-[0.05em] transition-colors ${cat === c ? "bg-electric text-black font-semibold" : "border border-white/10 text-secondary2 hover:text-white"}`}>{c}</button>
              ))}
            </div>
          )}

          {shown.length === 0 ? (
            <p className="text-secondary2">Aucune vidéo pour le moment.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {shown.map((it, i) => (
                <Reveal key={it.id || i} delay={(i % 3) * 0.08}>
                  <button data-testid={`video-item-${i}`} onClick={() => showreel.open(it.videoUrl, it.title, it.category)}
                    className="group w-full text-left rounded-lg overflow-hidden border border-white/10 bg-surface/60 hover:border-electric/40 transition-colors">
                    <div className="relative aspect-video overflow-hidden bg-[#0e0e0e]">
                      {it.thumbnail ? <img src={resolveUrl(it.thumbnail)} alt={it.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="w-full h-full" />}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="h-16 w-16 rounded-full bg-black/40 border border-white/40 backdrop-blur flex items-center justify-center text-white group-hover:border-electric group-hover:text-electric transition-colors"><Play size={22} className="ml-1 fill-current" /></span>
                      </span>
                      {it.category && <span className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase text-electric bg-black/50 backdrop-blur px-2.5 py-1 rounded-full border border-electric/40">{it.category}</span>}
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif text-xl text-white">{it.title}</h3>
                      {it.description && <p className="text-sm text-secondary2 font-light mt-2 line-clamp-2">{it.description}</p>}
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default function VideosPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <ShowreelProvider>
          <Analytics />
          <VideosBody />
        </ShowreelProvider>
      </ReactLenis>
    </ContentProvider>
  );
}
